import React, { useState, useEffect, useCallback } from "react";
import DOMPurify from 'dompurify';
import {
  Box,
  Card,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tooltip,
  Link,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { LinearProgress } from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ArchitecturalLetterTemplate from "./Letters/ViolationLetterTemplate";
import ReactDOM from "react-dom";
import useCompanyLogo from "../../utils/useCompanyLogo";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDropzone } from "react-dropzone";
import { resizeImageFiles } from "../../utils/imageResize";

import {
  Info as InfoIcon,
  Category as CategoryIcon,
  Home as HomeIcon,
  CalendarToday as CalendarTodayIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  PendingActions as PendingActionsIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

import EditViolation from "./EditViolations";
import ImageAnnotatorCanvas from "./Details/ImageAnnotatorCanvas";
import api from "../../../src/utils/api";

const ViolationDetailsTab = ({
  violationDetails,
  imageURL,
  violationType,
  users,
  onAssign,
}) => {
  const logoUrl = useCompanyLogo();
  const [openImageModal, setOpenImageModal] = useState(false);
  const [pdfPreview, setPdfPreview] = useState({ open: false, src: "" });
  const [fileImagePreview, setFileImagePreview] = useState({
    open: false,
    src: "",
    title: "",
  });
  const [annotateFile, setAnnotateFile] = useState(null);
  const [lastCureRecord, setLastCureRecord] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [lowResUpload, setLowResUpload] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [isEditingCureDays, setIsEditingCureDays] = useState(false);
  const [editedDaysToCure, setEditedDaysToCure] = useState(
    violationDetails.days_to_cure || 0
  );
  const [extensionReason, setExtensionReason] = useState("");

  const [violationTypes, setViolationTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(
    violationDetails.ccr_violation_type_id || ""
  );
  const [selectedUser, setSelectedUser] = useState(
    violationDetails.resolved_by || ""
  );

  const currentType = violationTypes.find(
    (t) => String(t.id) === String(selectedType)
  );

  const [isEditingResolution, setIsEditingResolution] = useState(false);
  const [resolutionText, setResolutionText] = useState(
    violationDetails.resolution || ""
  );
  const [hasUserEditedResolution, setHasUserEditedResolution] = useState(
    Boolean(violationDetails.resolution)
  );

  const [letterLogs, setLetterLogs] = useState([]);
  const [files, setFiles] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    ...pageCardSx,
    p: 2.5,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  };

  const innerSoftSx = {
    borderRadius: "14px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(47,99,173,0.03)",
  };

  const sectionHeaderIconSx = {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  useEffect(() => {
    setSelectedType(violationDetails.ccr_violation_type_id || "");
    setSelectedUser(violationDetails.resolved_by || "");
    setEditedDaysToCure(violationDetails.days_to_cure || 0);
    setResolutionText(violationDetails.resolution || "");
    setHasUserEditedResolution(Boolean(violationDetails.resolution));
  }, [violationDetails]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await api.get("/getCcrViolationTypes");
        setViolationTypes(res.data || []);
      } catch (err) {
        console.error("Failed to load violation types", err);
      }
    };
    fetchTypes();
  }, []);

  useEffect(() => {
    if (!currentType) return;

    const existingResolution = violationDetails.resolution?.trim();
    const typeResolutionStep = currentType.resolution_step?.trim();

    if (!existingResolution && !hasUserEditedResolution && typeResolutionStep) {
      setResolutionText(typeResolutionStep);
    }
  }, [currentType, violationDetails.resolution, hasUserEditedResolution]);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await api.get(`/violations/${violationDetails.id}/letter-logs`);
        setLetterLogs(res.data || []);
      } catch (err) {
        console.error("Failed to load letter logs:", err);
      }
    };
    if (violationDetails?.id) fetchLogs();
  }, [violationDetails?.id]);

  const fetchFiles = async () => {
    try {
      const res = await api.get(`/violation-files/${violationDetails.id}`);
      setFiles(res.data || []);
    } catch (err) {
      console.error("Failed to load files:", err);
    }
  };

  useEffect(() => {
    const fetchLastCure = async () => {
      try {
        const res = await api.get(`/violations/${violationDetails.id}/last-cure`);
        setLastCureRecord(res.data);
      } catch (err) {
        console.error("Failed to load last cure record:", err);
      }
    };
    if (violationDetails?.id) fetchLastCure();
  }, [violationDetails?.id, violationDetails.status]);

  useEffect(() => {
    if (violationDetails?.id) fetchFiles();
  }, [violationDetails?.id]);

  const handleDownloadStoredPDF = async (log) => {
    try {
      if (!log.file_id) {
        setSnackbar({
          open: true,
          message: "This log entry does not have an attached file.",
          severity: "warning",
        });
        return;
      }

      const res = await api.get(`/violation-files/file/${log.file_id}`);
      const file = res.data;

      if (!file.file_url) {
        setSnackbar({
          open: true,
          message: "File URL missing in record.",
          severity: "error",
        });
        return;
      }

      const correctUrl = `${api.defaults.baseURL}/violationsletters/${
        file.violation_id
      }/${encodeURIComponent(file.file_url)}`;

      setPdfPreview({
        open: true,
        src: correctUrl,
      });
    } catch (err) {
      console.error("Failed to load stored file:", err);
      setSnackbar({
        open: true,
        message: "Unable to load stored PDF.",
        severity: "error",
      });
    }
  };

  const handleGeneratePDF = async (log) => {
    try {
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      document.body.appendChild(container);

      const element = (
        <ArchitecturalLetterTemplate
          type={log.template_name || "Violation Letter"}
          member={{
            FirstName: log.recipient_firstname,
            LastName: log.recipient_lastname,
            FullAddress: log.recipient_address,
            EmailAddress: log.recipient_email,
          }}
          requestDetails={{
            id: violationDetails.id,
            property_id: violationDetails.property_id,
            description: violationDetails.comment,
            status: violationDetails.status,
            priority: violationDetails.priority,
          }}
          date={log.created_at}
          content={log.template_body}
          logoUrl={logoUrl}
        />
      );

      ReactDOM.render(element, container);

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${log.template_name || "Violation_Letter"}.pdf`);

      ReactDOM.unmountComponentAtNode(container);
      document.body.removeChild(container);
    } catch (error) {
      console.error("PDF generation failed:", error);
      setSnackbar({
        open: true,
        message: "Failed to generate PDF.",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setLastCureRecord((prev) => (prev ? { ...prev } : prev));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDelete = async (file) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    try {
      await api.delete(`/violation-files/${file.id}`);
      fetchFiles();
      setSnackbar({ open: true, message: "File deleted", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Delete failed", severity: "error" });
    }
  };

  const handleUpdateResolution = async () => {
    try {
      const response = await api.put(`/editViolation/${violationDetails.id}`, {
        ...violationDetails,
        resolution: resolutionText,
      });

      if (response.status === 200) {
        violationDetails.resolution = resolutionText;
        setHasUserEditedResolution(true);
        setSnackbar({
          open: true,
          message: "Resolution updated successfully.",
          severity: "success",
        });
        setIsEditingResolution(false);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to update resolution.",
        severity: "error",
      });
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles?.length) return;
    const files = lowResUpload ? await resizeImageFiles(acceptedFiles) : acceptedFiles;
    setUploadQueue((prev) => [...prev, ...files]);
  }, [lowResUpload]);

  const handleRemoveQueuedFile = (index) => {
    setUploadQueue((prev) => prev.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
    },
  });

  const handleUploadQueuedFiles = async () => {
    if (!uploadQueue.length) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("request_id", violationDetails.id);
      uploadQueue.forEach((f) => fd.append("upload_urls", f));

      try {
        await api.post("/violation-files", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (multiErr) {
        for (const file of uploadQueue) {
          const single = new FormData();
          single.append("file", file);

          await api.post(`/violation-files/${violationDetails.id}`, single, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }

      setUploadQueue([]);
      await fetchFiles();
      setSnackbar({ open: true, message: "Files uploaded", severity: "success" });
    } catch (err) {
      console.error("Upload failed:", err);
      setSnackbar({ open: true, message: "Upload failed", severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const getCureProgress = () => {
    if (!lastCureRecord || !lastCureRecord.recorded_at) return null;

    const startDate = new Date(lastCureRecord.recorded_at);
    const totalDays =
      lastCureRecord.days_to_cure || violationDetails.days_to_cure || 0;
    if (!totalDays) return null;

    const now = new Date();
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysPassed = (now - startDate) / msPerDay;
    const daysRemaining = totalDays - daysPassed;
    const percent = Math.min(100, Math.max(0, (daysPassed / totalDays) * 100));

    return {
      daysPassed,
      daysRemaining,
      overdue: daysRemaining < 0,
      percent,
    };
  };

  const handleAssignViolation = async (userId) => {
    try {
      const response = await api.put("/assignViolation", {
        violation_id: violationDetails.id,
        assign_to: userId,
      });

      if (response.status === 200) {
        setSelectedUser(userId);
        onAssign(userId);
        setSnackbar({
          open: true,
          message: "Violation assigned and email sent.",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to assign violation.",
        severity: "error",
      });
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "N/A";
    const [year, month, day] = dateString.split("T")[0].split("-");
    return `${month}/${day}/${year}`;
  };

  const handleTypeChange = async (typeId) => {
    try {
      await api.put("/updateViolationType", {
        violation_id: violationDetails.id,
        ccr_violation_type_id: typeId,
      });

      setSelectedType(typeId);

      const selectedViolationType = violationTypes.find(
        (type) => String(type.id) === String(typeId)
      );

      if (
        selectedViolationType?.resolution_step &&
        !violationDetails.resolution &&
        !hasUserEditedResolution
      ) {
        setResolutionText(selectedViolationType.resolution_step);
      }

      setSnackbar({
        open: true,
        message: "Violation type updated successfully.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to update violation type.",
        severity: "error",
      });
    }
  };

  const SectionTitle = ({ icon, title, action = null }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        mb: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <Box sx={sectionHeaderIconSx}>{icon}</Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
        >
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Card sx={{ ...sectionCardSx, display: "flex", justifyContent: "center" }}>
        <EditViolation
          violationDetails={violationDetails}
          onUpdate={(updatedData) => {
            Object.assign(violationDetails, updatedData);
          }}
        />
      </Card>

      <Box sx={{ textAlign: "center" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#6b778c" }}>
          Violation ID: <strong>VLT-{violationDetails.id}</strong>
        </Typography>
      </Box>

      <Grid
  container
  rowSpacing={3}
  columnSpacing={8}
  sx={{
    alignItems: "flex-start",
  }}
>
        {imageURL && (
          <Grid item xs={12}>
            <Card sx={{ ...sectionCardSx, width: "100%", textAlign: "center" }}>
              <SectionTitle
                icon={<InfoIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                title="Violation Image"
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 1,
                  cursor: "pointer",
                }}
                onClick={() => setOpenImageModal(true)}
              >
                <Box
                  component="img"
                  src={imageURL}
                  alt="Violation"
                  sx={{
                    width: "200px",
                    borderRadius: "10px",
                    border: "2px solid rgba(47,99,173,0.12)",
                    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.08)",
                  }}
                />
              </Box>

              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Click image to enlarge
              </Typography>
            </Card>
          </Grid>
        )}

        <Grid item xs={14} md={6}>
          <Card sx={{ ...sectionCardSx, width: "100%" }}>
            <SectionTitle
              icon={<CategoryIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Violation Type"
            />

            <TextField
              select
              label="Select Type"
              value={selectedType}
              onChange={(e) => handleTypeChange(e.target.value)}
              fullWidth
              size="small"
              sx={textFieldSx}
            >
              {violationTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.title}
                </MenuItem>
              ))}
            </TextField>

            {currentType?.rule && (
              <Typography
                variant="body2"
                sx={{ mt: 1.25, color: "#6b778c", fontStyle: "italic", lineHeight: 1.6 }}
              >
                <strong>Rule:</strong> {currentType.rule}
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...sectionCardSx, width: "100%" }}>
            <SectionTitle
              icon={<PersonIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Assigned To"
            />

            <TextField
              select
              label="Select User"
              value={selectedUser}
              onChange={(e) => handleAssignViolation(e.target.value)}
              fullWidth
              size="small"
              sx={textFieldSx}
            >
              {users.map((user) => (
                <MenuItem key={user.UserID} value={user.UserID}>
                  {user.Name} {user.Lastname}
                </MenuItem>
              ))}
            </TextField>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...sectionCardSx, width: "100%" }}>
            <SectionTitle
              icon={<InfoIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Violation Details"
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
              <Typography variant="body2">
                <strong>Violation ID:</strong> VLT-{violationDetails.id}
              </Typography>
              <Typography variant="body2">
                <strong>Property ID:</strong> {violationDetails.property_id}
              </Typography>
              <Typography variant="body2">
                <strong>Created At:</strong>{" "}
                {violationDetails.created_at
                  ? new Date(violationDetails.created_at).toLocaleString()
                  : "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Violated At:</strong>{" "}
                {formatDateOnly(violationDetails.violated_at)}
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...sectionCardSx, width: "100%" }}>
            <SectionTitle
              icon={<CheckCircleIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Status"
            />
            <Typography variant="body2">{violationDetails.status || "N/A"}</Typography>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ ...sectionCardSx, width: "100%" }}>
            <SectionTitle
              icon={<CommentIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Comment"
            />
            <Typography variant="body2">
              {violationDetails.comment || "N/A"}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ ...sectionCardSx, width: "100%" }}>
            <SectionTitle
              icon={<DescriptionIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Resolution"
              action={
                !isEditingResolution ? (
                  <Tooltip title="Edit Resolution">
                    <IconButton size="small" onClick={() => setIsEditingResolution(true)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                ) : null
              }
            />

            {isEditingResolution ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <TextField
                  value={resolutionText}
                  onChange={(e) => {
                    setResolutionText(e.target.value);
                    setHasUserEditedResolution(true);
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Enter resolution details..."
                  sx={textFieldSx}
                />

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleUpdateResolution}
                    sx={primaryButtonSx}
                  >
                    Save
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloseIcon />}
                    onClick={() => {
                      setResolutionText(violationDetails.resolution || "");
                      setIsEditingResolution(false);
                    }}
                    sx={secondaryButtonSx}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ fontStyle: resolutionText ? "normal" : "italic", lineHeight: 1.7 }}
              >
                {resolutionText || "No resolution yet"}
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...sectionCardSx, width: "100%" }}>
            <SectionTitle
              icon={<PendingActionsIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Days to Cure"
              action={
                !isEditingCureDays ? (
                  <Tooltip title="Edit Days to Cure">
                    <IconButton size="small" onClick={() => setIsEditingCureDays(true)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                ) : null
              }
            />

            {!isEditingCureDays ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {lastCureRecord?.days_to_cure ??
                    violationDetails.days_to_cure ??
                    "N/A"}{" "}
                  days
                </Typography>

                {lastCureRecord?.recorded_at && (
                  <Typography variant="caption" color="text.secondary">
                    Last updated {new Date(lastCureRecord.recorded_at).toLocaleString()}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <TextField
                  size="small"
                  type="number"
                  label="New Days to Cure"
                  value={editedDaysToCure}
                  onChange={(e) => setEditedDaysToCure(Number(e.target.value) || 0)}
                  sx={{ width: "180px", ...textFieldSx }}
                />

                <TextField
                  size="small"
                  label="Reason / Notes"
                  value={extensionReason}
                  onChange={(e) => setExtensionReason(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  sx={textFieldSx}
                />

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={async () => {
                      try {
                        const res = await api.put(
                          `/violations/${violationDetails.id}/update-cure-days`,
                          {
                            new_days_to_cure: editedDaysToCure,
                            user_id: violationDetails.resolved_by || violationDetails.created_by_user_id || null,
                            notes: extensionReason || "Manual extension approved",
                          }
                        );

                        setSnackbar({
                          open: true,
                          message: `Cure days updated (${res.data.restart_number} total restarts).`,
                          severity: "success",
                        });

                        const { data } = await api.get(
                          `/violations/${violationDetails.id}/last-cure`
                        );
                        setLastCureRecord(data);

                        setIsEditingCureDays(false);
                        setExtensionReason("");
                      } catch (err) {
                        console.error("Error updating cure days:", err);
                        setSnackbar({
                          open: true,
                          message: "Failed to update cure days.",
                          severity: "error",
                        });
                      }
                    }}
                    sx={primaryButtonSx}
                  >
                    Save
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloseIcon />}
                    onClick={() => {
                      setIsEditingCureDays(false);
                      setExtensionReason("");
                    }}
                    sx={secondaryButtonSx}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...sectionCardSx, width: "100%" }}>
            <SectionTitle
              icon={<CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Cure Progress"
            />

            {lastCureRecord && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Cure Restart Count:</strong> {lastCureRecord.restart_number}
                </Typography>
                <Typography variant="body2">
                  <strong>Last Restarted:</strong>{" "}
                  {new Date(lastCureRecord.recorded_at).toLocaleString()}
                </Typography>
              </Box>
            )}

            {(() => {
              const progress = getCureProgress();
              if (!progress) return <Typography variant="body2">N/A</Typography>;

              return (
                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress.percent}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#eee",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: progress.overdue ? "#d32f2f" : "#2e7d32",
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {progress.overdue ? (
                      <span style={{ color: "#d32f2f" }}>
                        Overdue by {Math.abs(progress.daysRemaining).toFixed(1)} day
                        {Math.abs(progress.daysRemaining) >= 1.5 && "s"}
                      </span>
                    ) : (
                      <span style={{ color: "#2e7d32" }}>
                        {progress.daysRemaining.toFixed(1)} day
                        {progress.daysRemaining >= 1.5 && "s"} remaining
                      </span>
                    )}
                  </Typography>
                </Box>
              );
            })()}
          </Card>
        </Grid>

        <Grid item xs={12} >
          <Card sx={{ ...sectionCardSx, width: "100%" }}>
            <SectionTitle
              icon={<UploadIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Files"
            />

            <Typography variant="body2" sx={{ mb: 1.25, color: "#6b778c" }}>
              Attach files to this violation
            </Typography>

            <FormControlLabel
              sx={{ mb: 1, ml: 0 }}
              control={
                <Switch
                  checked={lowResUpload}
                  onChange={(e) => setLowResUpload(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#223142" }}>
                  {lowResUpload ? "Low resolution (faster upload)" : "Full resolution"}
                </Typography>
              }
            />

            <Box
              {...getRootProps()}
              sx={{
                ...innerSoftSx,
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "#e3f2fd" : "rgba(47,99,173,0.02)",
                mb: 2,
                borderStyle: "dashed",
                borderWidth: "2px",
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {isDragActive
                  ? "Drop files here..."
                  : "Drag & drop multiple files here, or click to select"}
              </Typography>
            </Box>

            {uploadQueue.length > 0 && (
              <Box sx={{ ...innerSoftSx, p: 1, maxHeight: 150, overflowY: "auto", mb: 2 }}>
                {uploadQueue.map((file, idx) => (
                  <Box
                    key={`${file.name}-${idx}`}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      borderBottom:
                        idx !== uploadQueue.length - 1
                          ? "1px solid rgba(47,99,173,0.08)"
                          : "none",
                      py: 0.75,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: "80%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </Typography>

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveQueuedFile(idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={handleUploadQueuedFiles}
                disabled={uploading || uploadQueue.length === 0}
                startIcon={uploading ? null : <UploadIcon />}
                sx={primaryButtonSx}
              >
                {uploading ? "Uploading..." : `Upload ${uploadQueue.length || ""}`.trim()}
              </Button>

              {uploadQueue.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setUploadQueue([])}
                  disabled={uploading}
                  sx={secondaryButtonSx}
                >
                  Clear
                </Button>
              )}
            </Box>

            <Grid container spacing={3}>
              {files.map((file) => {
                const fileType = file.file_type || "";
                const isImage = fileType.startsWith("image/");
                const isPDF =
                  fileType.includes("pdf") ||
                  file.file_name?.toLowerCase().endsWith(".pdf");
                const fileUrl = `${api.defaults.baseURL}/violationsimages/${
                  violationDetails.id
                }/${encodeURIComponent(file.file_name)}`;

                return (
                  <Grid item xs={12} sm={6} md={4} key={file.id}>
                    <Card
                      sx={{
                        ...innerSoftSx,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        textAlign: "center",
                        height: "100%",
                      }}
                    >
                      {isImage ? (
                        <>
                          <Box
                            component="img"
                            src={fileUrl}
                            alt={file.file_name}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: 120,
                              borderRadius: "8px",
                              cursor: "pointer",
                              objectFit: "cover",
                            }}
                            onClick={() =>
                              setFileImagePreview({
                                open: true,
                                src: fileUrl,
                                title: file.file_name,
                              })
                            }
                          />
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.5, wordBreak: "break-word" }}
                          >
                            {file.file_name}
                          </Typography>

                          <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              onClick={() =>
                                setFileImagePreview({
                                  open: true,
                                  src: fileUrl,
                                  title: file.file_name,
                                })
                              }
                              sx={secondaryButtonSx}
                            >
                              Preview
                            </Button>

                            <Button
                              size="small"
                              variant="contained"
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              sx={primaryButtonSx}
                            >
                              Download
                            </Button>
                          </Box>
                        </>
                      ) : isPDF ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 2,
                          }}
                        >
                          <PdfIcon color="error" sx={{ fontSize: 40 }} />
                          <Typography
                            variant="body2"
                            sx={{ mt: 1, wordBreak: "break-word" }}
                          >
                            {file.file_name}
                          </Typography>
                          <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                setPdfPreview({ open: true, src: fileUrl })
                              }
                              sx={secondaryButtonSx}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              sx={primaryButtonSx}
                            >
                              Download
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, wordBreak: "break-word" }}
                        >
                          {file.file_name}
                        </Typography>
                      )}

                      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        {isImage && (
                          <Tooltip title="Annotate">
                            <IconButton
                              size="small"
                              onClick={() => setAnnotateFile(file)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(file)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ ...sectionCardSx, mt: 1 }}>
        <SectionTitle
          icon={<PdfIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
          title="Letter History"
        />

        {letterLogs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No letters have been sent or exported yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {letterLogs.map((log) => (
              <Accordion
                key={log.id}
                sx={{
                  borderRadius: "12px !important",
                  boxShadow: "none",
                  border: "1px solid rgba(47,99,173,0.08)",
                  overflow: "hidden",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ flexGrow: 1 }}>
                    {log.action === "email" ? <EmailIcon /> : <PdfIcon />}{" "}
                    <strong>{log.template_name || "Violation Letter"}</strong> -{" "}
                    {new Date(log.created_at).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sent by {log.sent_by_firstname} {log.sent_by_lastname}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Recipient:</strong> {log.recipient || "N/A"} <br />
                    <strong>Address Type:</strong> {log.address_type || "N/A"}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  <Box
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      padding: 2,
                      backgroundColor: "#fafafa",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(log.template_body || '') }}
                  />

                  <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PdfIcon />}
                      onClick={() => handleDownloadStoredPDF(log)}
                      sx={secondaryButtonSx}
                    >
                      Regenerate PDF
                    </Button>

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PdfIcon />}
                      onClick={() => handleGeneratePDF(log)}
                      sx={primaryButtonSx}
                    >
                      Download New PDF
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Card>

      <Dialog
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        maxWidth="md"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setOpenImageModal(false)}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={imageURL}
            alt="Violation Full"
            sx={{ width: "100%", height: "auto", borderRadius: "10px" }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(fileImagePreview.open)}
        onClose={() => setFileImagePreview({ open: false, src: "", title: "" })}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent
          sx={{
            position: "relative",
            backgroundColor: "#111",
            p: 2,
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={() => setFileImagePreview({ open: false, src: "", title: "" })}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.45)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <Box
            component="img"
            src={fileImagePreview.src}
            alt={fileImagePreview.title || "Preview"}
            sx={{
              maxWidth: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "10px",
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(pdfPreview.open)}
        onClose={() => setPdfPreview({ open: false, src: "" })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "black",
            p: 2,
          }}
        >
          <IconButton
            onClick={() => setPdfPreview({ open: false, src: "" })}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(0,0,0,0.4)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <Box
            sx={{
              width: "100%",
              height: "90vh",
              maxWidth: "1200px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <object
              data={pdfPreview.src}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            >
              <Typography color="white">
                PDF preview not supported.{" "}
                <Link
                  href={pdfPreview.src}
                  target="_blank"
                  rel="noopener"
                  sx={{ color: "#90caf9" }}
                >
                  Open PDF
                </Link>
              </Typography>
            </object>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              href={pdfPreview.src}
              download
              target="_blank"
              rel="noopener"
              sx={primaryButtonSx}
            >
              Download PDF
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(annotateFile)}
        onClose={() => setAnnotateFile(null)}
        fullScreen
      >
        {annotateFile && (
          <ImageAnnotatorCanvas
            file={annotateFile}
            imageSrc={`${api.defaults.baseURL}/violationsimages/${
              violationDetails.id
            }/${encodeURIComponent(annotateFile.file_name)}`}
            requestId={violationDetails.id}
            onClose={() => setAnnotateFile(null)}
          />
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViolationDetailsTab;